import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, Home, Info, HelpCircle, BookOpen, Calculator, MapPin } from 'lucide-react';

export default function MobileNav() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const drawerRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)');
    const closeOnDesktop = () => { if (desktop.matches) setIsOpen(false); };
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const opener = openerRef.current;
    const timer = window.setTimeout(() => drawerRef.current?.querySelector<HTMLButtonElement>('button')?.focus(), 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setIsOpen(false); return; }
      if (event.key !== 'Tab') return;
      const items = drawerRef.current?.querySelectorAll<HTMLElement>('button, a[href]');
      if (!items?.length) return;
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => { clearTimeout(timer); document.removeEventListener('keydown', onKeyDown); opener?.focus(); };
  }, [isOpen]);

  const navLinks = [
    { to: '/', label: 'Home', icon: Home },
    { to: '/about', label: 'About', icon: Info },
    { to: '/canadian-real-estate', label: 'Canadian Real Estate', icon: MapPin },
    { to: '/roi-calculator', label: 'ROI Calculator', icon: Calculator },
    { to: '/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/blog', label: 'Blog', icon: BookOpen },
    { to: '/book', label: 'Book a Call', icon: Phone, primary: true },
  ];

  return (
    <div className="lg:hidden">
      {/* Hamburger Button */}
      <button
        ref={openerRef}
        aria-controls="mobile-navigation-drawer"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-3 touch-manipulation min-h-[56px] min-w-[56px] flex items-center justify-center"
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
      >
        <div className="relative w-6 h-6">
          <motion.span
            animate={isOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="absolute top-0 left-0 w-full h-0.5 bg-black rounded-full origin-center"
          />
          <motion.span
            animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="absolute top-1/2 left-0 w-full h-0.5 bg-black rounded-full -translate-y-1/2"
          />
          <motion.span
            animate={isOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="absolute bottom-0 left-0 w-full h-0.5 bg-black rounded-full origin-center"
          />
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
              aria-hidden="true"
            />

            {/* Drawer */}
            <motion.div
              ref={drawerRef}
              id="mobile-navigation-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white shadow-2xl z-[70] overflow-y-auto pt-safe pb-safe"
            >
              <div className="flex flex-col min-h-full">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-2 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900">Menu</h2>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-3 -mr-3 touch-manipulation min-h-[56px] min-w-[56px] flex items-center justify-center"
                    aria-label="Close menu"
                  >
                    <X className="w-6 h-6 text-gray-900" />
                  </button>
                </div>

                {/* Navigation Links */}
                <nav className="flex-1 py-3 px-4" role="navigation" aria-label="Mobile navigation">
                  <ul className="space-y-1">
                    {navLinks.map((link) => {
                      const Icon = link.icon;
                      const isActive = location.pathname === link.to;
                      const isPrimary = link.primary;

                      if (isPrimary) {
                        return (
                          <li key={link.to} className="pt-3">
                            <Link
                              to={link.to}
                              onClick={() => setIsOpen(false)}
                              aria-current={location.pathname === link.to ? "page" : undefined}
                              className="group relative overflow-hidden flex items-center gap-4 px-4 py-3 rounded-md bg-gradient-to-br from-[#d4a10d] via-[#c49e0c] to-[#b8900b] text-white font-bold text-lg shadow-lg min-h-[52px] touch-manipulation"
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-active:translate-x-[200%] transition-transform duration-700" />
                              <Icon className="w-6 h-6 relative z-10" />
                              <span className="relative z-10 tracking-wide">{link.label}</span>
                            </Link>
                          </li>
                        );
                      }

                      return (
                        <li key={link.to}>
                          <Link
                            to={link.to}
                              onClick={() => setIsOpen(false)}
                              aria-current={location.pathname === link.to ? "page" : undefined}
                            className={`flex items-center gap-4 px-4 py-2 rounded-md text-base font-semibold transition-all duration-200 min-h-[48px] touch-manipulation ${
                              isActive
                                ? 'bg-[#d4a10d]/10 text-[#806008]'
                                : 'text-gray-700 active:bg-gray-50'
                            }`}
                          >
                            <Icon className={`w-5 h-5 ${isActive ? 'text-[#d4a10d]' : 'text-gray-400'}`} />
                            <span>{link.label}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </nav>

                {/* Footer */}
                <div className="px-5 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-sm text-gray-600 text-center">Point Break Systems</p>
                  <p className="text-xs text-gray-500 text-center mt-1">AI Voice Callers for Real Estate</p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
